import { useState, useCallback } from 'react'
import req from '@sportninja/common/api/request'

/**
 * Response type for the onboarding API endpoint
 */
type OnboardingResponse = {
  status: string
  message: string
  onboarded: boolean
  can_accept_payments: boolean
  url?: string
  // Stripe-specific
  charges_enabled?: boolean
  payouts_enabled?: boolean
  details_submitted?: boolean
  currently_due?: string[]
  past_due?: string[]
  pending_verification?: string[]
  disabled_reason?: string
  // SportsPay-specific
  merchant_id?: string
  raw_status?: string
}

/**
 * Account details for payment provider (unified type for both Stripe and SportsPay)
 */
export type AccountDetails = {
  status: string
  message: string
  verified: boolean
  onboarded: boolean
  can_accept_payments: boolean
  // Stripe-specific
  charges_enabled?: boolean
  payouts_enabled?: boolean
  details_submitted?: boolean
  account?: {
    id: string
    object: string
    business_profile: {
      mcc: string
      name: string
      support_address: {
        city: string
        country: string
        line1: string
        line2: any | null
        postal_code: string
        state: string
      }
      support_email: any | null
      support_phone: string
      support_url: any | null
      url: string
    }
    capabilities: {
      card_payments: string
      transfers: string
      [key: string]: string
    }
    charges_enabled: boolean
    country: string
    default_currency: string
    details_submitted: boolean
    email: string
    payouts_enabled: boolean
    type: string
  }
  // SportsPay-specific
  merchant_id?: string
}

/**
 * Error type for payment providers
 */
export type ProviderError = {
  message: string
  code?: string
  type?: string
}

export enum PaymentProviderStatus {
  // Shared
  NOT_STARTED = 'not_started',
  ACTIVE = 'active',
  ERROR = 'error',
  // Stripe-specific
  STARTED = 'started',
  PENDING_VERIFICATION = 'pending_verification',
  ACTION_REQUIRED = 'action_required',
  RESTRICTED = 'restricted',
  DISABLED = 'disabled',
  // SportsPay-specific
  INCOMPLETE = 'incomplete',
  IN_REVIEW = 'in_review',
  SUSPENDED = 'suspended',
  // System
  NOT_CONFIGURED = 'not_configured',
}

export enum PaymentProviderId {
  SPORTSPAY = '2',
  STRIPE = '1',
}

/**
 * Custom hook to manage payment provider connections
 */
const usePaymentProviderConnection = () => {
  // Payment Provider states
  const [loading, setLoading] = useState(true)
  const [link, setLink] = useState<string | null>(null)
  const [isOnboarded, setIsOnboarded] = useState(false)
  const [accountStatus, setAccountStatus] =
    useState<PaymentProviderStatus | null>(null)
  const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(
    null
  )
  const [canAcceptPayments, setCanAcceptPayments] = useState(false)
  const [error, setError] = useState<boolean>(false)
  const [errorDetails, setErrorDetails] = useState<ProviderError | null>(null)
  const [providerMessage, setProviderMessage] = useState<string | null>(null)

  /**
   * Updates account information to the payment provider
   * @param code - SportsPay's `code` callback param; sent as
   *   `sportspay_terminal_id` (it was `sportspay_merchant_id` until the API
   *   renamed the field)
   * @param status - The status to post
   * @param accountId - The account ID to post to
   * @param paymentProviderId - The payment provider ID to post to
   * @returns 200
   */
  const putAccountInfo = useCallback(
    async (
      code: string,
      status: string,
      accountId: string,
      paymentProviderId: PaymentProviderId
    ): Promise<OnboardingResponse> => {
      const response: OnboardingResponse = await req(
        `/account/${accountId}/payment_provider/onboarding`,
        {
          method: 'PUT',
          body: JSON.stringify({
            sportspay_terminal_id: code,
            sportspay_account_status: status,
            payment_provider_type_id: paymentProviderId,
          }),
        }
      )
      return response
    },
    []
  )

  /**
   * Updates account information to the payment provider for Stripe onboarding
   * @param accountId - The account ID to post to
   * @param paymentProviderId - The payment provider ID to post to
   * @returns 200
   */
  const putAccountInfoForStripeOnboarding = useCallback(
    async (
      accountId: string,
      paymentProviderId: PaymentProviderId
    ): Promise<OnboardingResponse> => {
      const response: OnboardingResponse = await req(
        `/account/${accountId}/payment_provider/onboarding`,
        {
          method: 'PUT',
          body: JSON.stringify({
            payment_provider_type_id: paymentProviderId,
          }),
        }
      )
      return response
    },
    []
  )

  /**
   * Fetches Payment Provider URL
   * @param accountId - The account ID to fetch information for
   * @returns Response containing onboarding status and URLs
   */
  const fetchPaymentProviderUrl = useCallback(
    async (
      accountId: string,
      providerId: string
    ): Promise<OnboardingResponse> => {
      try {
        // /account/{accountid}/payment_provider/1/url
        const response: OnboardingResponse = await req(
          `/account/${accountId}/payment_provider/${providerId}/url`
        )
        return response
      } catch (e: any) {
        throw new Error(JSON.stringify(e))
      }
    },
    []
  )

  /**
   * Fetches connected account information for both payment providers
   * @param accountId - The account ID to fetch information for
   * @returns Response containing onboarding status and URLs
   */
  const fetchPaymentProvider = async (
    accountId: string
  ): Promise<OnboardingResponse> => {
    try {
      const response: OnboardingResponse = await req(
        `/account/${accountId}/payment_provider/onboarding`
      )
      return response
    } catch (e: any) {
      throw new Error(JSON.stringify(e))
    }
  }

  /**
   * Fetches account details
   * @param accountId - The account ID to fetch details for
   * @returns account details
   */
  const fetchAccountInfo = async (
    accountId: string
  ): Promise<AccountDetails> => {
    try {
      const response: AccountDetails = await req(
        `/account/${accountId}/payment_provider/details`
      )
      return response
    } catch (e: any) {
      throw new Error(JSON.stringify(e))
    }
  }

  /**
   * Handles error parsing and setting for payment providers
   */
  const handleProviderError = (_error: any) => {
    setError(true)
    try {
      if (typeof _error.message === 'string') {
        const parsedError = JSON.parse(_error.message)
        setErrorDetails({
          message: parsedError.message || 'Unknown Error Occurred',
          code: parsedError.code,
          type: parsedError.type,
        })
      } else {
        setErrorDetails({
          message: 'Failed To Connect To Payment Provider',
        })
      }
    } catch (parseError) {
      setErrorDetails({
        message: _error.message || 'Failed To Connect To Payment Provider',
      })
    }
  }

  /**
   * Fetches data for both payment providers
   * @param accountId - The account ID to fetch data for
   */
  const fetchPaymentProvidersData = useCallback(async (accountId: string) => {
    if (!accountId) {
      setLoading(false)
      return
    }

    // Reset error states before fetching
    setError(false)
    setErrorDetails(null)
    setProviderMessage(null)
    setLoading(true)

    try {
      const response = await fetchPaymentProvider(accountId)

      setAccountStatus(response.status as PaymentProviderStatus)
      setCanAcceptPayments(response.can_accept_payments ?? false)
      if (response.message) {
        setProviderMessage(response.message)
      }

      // Handle Onboarding
      if (!response.onboarded) {
        setLink(response.url || null)
        setIsOnboarded(false)
      } else {
        setIsOnboarded(true)
        try {
          const details = await fetchAccountInfo(accountId)
          setAccountDetails(details)
        } catch (e: any) {
          handleProviderError(e)
        }
      }
    } catch (e: any) {
      handleProviderError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Resets all state values to their defaults
   */
  const resetState = useCallback(() => {
    // Reset States
    setLoading(true)
    setLink(null)
    setIsOnboarded(false)
    setAccountDetails(null)
    setCanAcceptPayments(false)
    setError(false)
    setErrorDetails(null)
    setProviderMessage(null)
  }, [])

  return {
    // Payment Provider related states and functions
    accountStatus,
    canAcceptPayments,
    loading,
    link,
    isOnboarded,
    accountDetails,
    error,
    errorDetails,
    providerMessage,

    // Functions
    fetchPaymentProvidersData,
    resetState,
    putAccountInfo,
    putAccountInfoForStripeOnboarding,
    fetchPaymentProviderUrl,
  }
}

export default usePaymentProviderConnection
